
import { useEffect, useState } from 'react';

export default function HomePage() {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'bg-white shadow-md' : 'bg-transparent'}`}>
        <div className="max-w-7xl mx-auto px-10 py-6 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="https://public.readdy.ai/ai/img_res/3f5819b0-59b2-419a-bc90-4b01c3e25d98.png" alt="DINAGRO S.R.L." className="h-12 w-12 object-contain" />
            <span className={`text-2xl font-bold transition-colors ${scrolled ? 'text-gray-900' : 'text-white'}`}>DINAGRO S.R.L.</span>
          </div>
          <div className="flex items-center gap-12">
            <button onClick={() => scrollToSection('home')} className={`text-base font-medium transition-colors hover:opacity-70 cursor-pointer ${scrolled ? 'text-gray-800' : 'text-white'}`}>Home</button>
            <button onClick={() => scrollToSection('about')} className={`text-base font-medium transition-colors hover:opacity-70 cursor-pointer ${scrolled ? 'text-gray-800' : 'text-white'}`}>About</button>
            <button onClick={() => scrollToSection('products')} className={`text-base font-medium transition-colors hover:opacity-70 cursor-pointer ${scrolled ? 'text-gray-800' : 'text-white'}`}>Products</button>
            <button onClick={() => scrollToSection('features')} className={`text-base font-medium transition-colors hover:opacity-70 cursor-pointer ${scrolled ? 'text-gray-800' : 'text-white'}`}>Features</button>
            <button onClick={() => scrollToSection('contact')} className={`text-base font-medium transition-colors hover:opacity-70 cursor-pointer ${scrolled ? 'text-gray-800' : 'text-white'}`}>Contact</button>
          </div>
          <div className="w-12 h-12 rounded-full bg-gradient-to-br from-purple-600 to-blue-500 flex items-center justify-center">
            <i className="ri-shield-check-line text-white text-xl"></i>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section id="home" className="relative min-h-screen flex items-center justify-center m-8 rounded-3xl overflow-hidden bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: 'linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)',
            backgroundSize: '50px 50px'
          }}></div>
        </div>
        <div className="absolute bottom-0 right-0 w-96 h-96 opacity-20">
          <svg viewBox="0 0 200 200" className="w-full h-full">
            <path d="M 20 100 Q 60 20, 100 60 T 180 100" stroke="url(#gradient)" strokeWidth="3" fill="none" />
            <path d="M 20 120 Q 60 40, 100 80 T 180 120" stroke="url(#gradient)" strokeWidth="3" fill="none" />
            <defs>
              <linearGradient id="gradient" x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" stopColor="#6B46C1" />
                <stop offset="100%" stopColor="#3B82F6" />
              </linearGradient>
            </defs>
          </svg>
        </div>
        <div className="relative z-10 text-center px-8 max-w-5xl">
          <div className="inline-block px-6 py-2 rounded-full border-2 border-white/30 bg-white/10 backdrop-blur-sm mb-8">
            <span className="text-white text-sm uppercase tracking-wider font-medium">iOS Development Experts</span>
          </div>
          <h1 className="text-7xl font-bold text-white leading-tight mb-10" style={{ letterSpacing: '-1px' }}>
            Modern Mobile<br />
            Security Solutions<br />
            for iOS Platform
          </h1>
          <p className="text-xl text-white/80 font-light max-w-2xl mx-auto">
            Enhancing user experience through innovative VPN and Cleaner applications
          </p>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-32 px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-2 gap-16 items-center">
            <div className="relative">
              <div className="absolute -left-16 w-full h-full">
                <img src="https://public.readdy.ai/ai/img_res/e734dccc0ecd13e6e83339ddc27df56b.jpg" alt="DINAGRO Team" className="w-full h-full object-cover rounded-3xl shadow-2xl" />
              </div>
            </div>
            <div className="bg-gray-900 rounded-3xl p-16 relative">
              <div className="text-amber-500 text-9xl font-serif leading-none mb-6">"</div>
              <p className="text-white text-2xl leading-relaxed mb-8">
                DINAGRO S.R.L. represents a team of experts dedicated to developing cutting-edge applications for the iOS platform. Our commitment to excellence drives us to create products that prioritize user security and seamless mobile experiences.
              </p>
              <div className="mb-8">
                <p className="text-white text-lg font-semibold mb-1">Expert Development Team</p>
                <p className="text-white/70 text-sm">iOS Platform Specialists</p>
              </div>
              <div className="inline-block px-8 py-3 rounded-full bg-pink-300">
                <span className="text-gray-900 font-semibold">Since 2020</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Products Section */}
      <section id="products" className="py-32 px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-end justify-between mb-16">
            <h2 className="text-6xl font-bold text-gray-900 max-w-md font-serif">Our Products</h2>
            <p className="text-lg text-gray-600 max-w-md leading-relaxed">
              VPN and Cleaner applications designed to enhance mobile security and optimize device performance
            </p>
          </div>
          <div className="grid grid-cols-12 gap-6">
            <div className="col-span-5 bg-slate-900 rounded-2xl overflow-hidden relative h-[600px] group cursor-pointer">
              <img src="https://readdy.ai/api/search-image?query=VPN%20application%20interface%20on%20iPhone%20screen%20showing%20secure%20connection%20with%20blue%20glowing%20effects%2C%20digital%20security%20visualization%20with%20encrypted%20data%20streams%2C%20network%20protection%20graphics%2C%20modern%20mobile%20app%20UI%20with%20dark%20theme%2C%20cybersecurity%20concept%20with%20shield%20icons%20and%20lock%20symbols%2C%20professional%20tech%20illustration&width=800&height=1000&seq=dinagro-vpn-main&orientation=portrait" alt="VPN Application" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
              <div className="absolute inset-0 bg-gradient-to-t from-slate-900/90 via-slate-900/40 to-transparent"></div>
              <div className="absolute bottom-0 left-0 p-8">
                <div className="inline-block px-4 py-1 rounded-full bg-blue-500 mb-4">
                  <span className="text-white text-sm font-medium">Security</span>
                </div>
                <h3 className="text-white text-3xl font-bold mb-3">VPN Application</h3>
                <p className="text-white text-base leading-relaxed max-w-md">
                  Secure your connection with military-grade encryption and global server access
                </p>
              </div>
            </div>
            <div className="col-span-3 bg-stone-50 rounded-2xl p-8 flex flex-col items-center justify-center h-[600px] group cursor-pointer hover:shadow-xl transition-shadow">
              <div className="w-32 h-32 rounded-3xl bg-gradient-to-br from-purple-600 to-blue-500 mb-6 flex items-center justify-center shadow-lg">
                <i className="ri-shield-keyhole-line text-white text-6xl"></i>
              </div>
              <div className="inline-block px-3 py-1 rounded-full bg-green-100 mb-4">
                <span className="text-green-700 text-xs font-semibold flex items-center gap-1">
                  <i className="ri-shield-check-line"></i> Active
                </span>
              </div>
              <h4 className="text-gray-900 text-2xl font-bold mb-2">VPN Pro</h4>
              <p className="text-gray-500 text-sm">iOS 15+</p>
            </div>
            <div className="col-span-4 bg-stone-50 rounded-2xl p-8 flex flex-col items-center justify-center h-[600px] group cursor-pointer hover:shadow-xl transition-shadow">
              <div className="w-32 h-32 rounded-3xl bg-gradient-to-br from-orange-500 to-pink-500 mb-6 flex items-center justify-center shadow-lg">
                <i className="ri-delete-bin-line text-white text-6xl"></i>
              </div>
              <div className="inline-block px-3 py-1 rounded-full bg-orange-100 mb-4">
                <span className="text-orange-700 text-xs font-semibold flex items-center gap-1">
                  <i className="ri-flashlight-line"></i> Optimized
                </span>
              </div>
              <h4 className="text-gray-900 text-2xl font-bold mb-2">Cleaner Plus</h4>
              <p className="text-gray-500 text-sm">iOS 15+</p>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-32 px-8 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-20">
            <h2 className="text-6xl font-bold text-gray-900 mb-4 inline-flex items-center gap-3">
              Key Features <span className="text-5xl">🚀</span>
            </h2>
            <p className="text-lg text-gray-600 mt-6">Powerful capabilities for modern mobile security</p>
          </div>
          <div className="grid grid-cols-3 gap-8">
            <div className="bg-white rounded-2xl p-8 shadow-sm hover:shadow-xl transition-shadow">
              <div className="w-16 h-16 rounded-full bg-gradient-to-br from-purple-500 to-purple-600 flex items-center justify-center mb-6">
                <i className="ri-lock-line text-white text-3xl"></i>
              </div>
              <h3 className="text-gray-900 text-xl font-bold mb-3">Military-Grade Encryption</h3>
              <p className="text-gray-600 text-sm leading-relaxed mb-4">
                Protect your data with AES-256 encryption, the same standard used by governments and security experts worldwide.
              </p>
              <ul className="space-y-2">
                <li className="flex items-start gap-2 text-sm text-gray-700">
                  <i className="ri-check-line text-purple-500 mt-0.5"></i>
                  <span>Bank-level security protocols</span>
                </li>
                <li className="flex items-start gap-2 text-sm text-gray-700">
                  <i className="ri-check-line text-purple-500 mt-0.5"></i>
                  <span>Zero-log policy guaranteed</span>
                </li>
              </ul>
            </div>
            <div className="bg-white rounded-2xl p-8 shadow-sm hover:shadow-xl transition-shadow">
              <div className="w-16 h-16 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center mb-6">
                <i className="ri-global-line text-white text-3xl"></i>
              </div>
              <h3 className="text-gray-900 text-xl font-bold mb-3">Global Server Network</h3>
              <p className="text-gray-600 text-sm leading-relaxed mb-4">
                Connect to high-speed servers across 50+ countries for unrestricted access to content worldwide.
              </p>
              <ul className="space-y-2">
                <li className="flex items-start gap-2 text-sm text-gray-700">
                  <i className="ri-check-line text-blue-500 mt-0.5"></i>
                  <span>Unlimited bandwidth</span>
                </li>
                <li className="flex items-start gap-2 text-sm text-gray-700">
                  <i className="ri-check-line text-blue-500 mt-0.5"></i>
                  <span>Auto server selection</span>
                </li>
              </ul>
            </div>
            <div className="bg-white rounded-2xl p-8 shadow-sm hover:shadow-xl transition-shadow">
              <div className="w-16 h-16 rounded-full bg-gradient-to-br from-green-500 to-green-600 flex items-center justify-center mb-6">
                <i className="ri-speed-line text-white text-3xl"></i>
              </div>
              <h3 className="text-gray-900 text-xl font-bold mb-3">Lightning Fast Performance</h3>
              <p className="text-gray-600 text-sm leading-relaxed mb-4">
                Experience blazing-fast connection speeds optimized for streaming, gaming, and browsing without lag.
              </p>
              <ul className="space-y-2">
                <li className="flex items-start gap-2 text-sm text-gray-700">
                  <i className="ri-check-line text-green-500 mt-0.5"></i>
                  <span>Ultra-low latency</span>
                </li>
                <li className="flex items-start gap-2 text-sm text-gray-700">
                  <i className="ri-check-line text-green-500 mt-0.5"></i>
                  <span>Optimized protocols</span>
                </li>
              </ul>
            </div>
            <div className="bg-white rounded-2xl p-8 shadow-sm hover:shadow-xl transition-shadow">
              <div className="w-16 h-16 rounded-full bg-gradient-to-br from-orange-500 to-orange-600 flex items-center justify-center mb-6">
                <i className="ri-delete-bin-line text-white text-3xl"></i>
              </div>
              <h3 className="text-gray-900 text-xl font-bold mb-3">Smart Device Cleaning</h3>
              <p className="text-gray-600 text-sm leading-relaxed mb-4">
                Remove junk files, optimize storage, and boost your device performance with intelligent cleaning algorithms.
              </p>
              <ul className="space-y-2">
                <li className="flex items-start gap-2 text-sm text-gray-700">
                  <i className="ri-check-line text-orange-500 mt-0.5"></i>
                  <span>Deep cache cleaning</span>
                </li>
                <li className="flex items-start gap-2 text-sm text-gray-700">
                  <i className="ri-check-line text-orange-500 mt-0.5"></i>
                  <span>Duplicate file detection</span>
                </li>
              </ul>
            </div>
            <div className="bg-white rounded-2xl p-8 shadow-sm hover:shadow-xl transition-shadow">
              <div className="w-16 h-16 rounded-full bg-gradient-to-br from-pink-500 to-pink-600 flex items-center justify-center mb-6">
                <i className="ri-cloud-line text-white text-3xl"></i>
              </div>
              <h3 className="text-gray-900 text-xl font-bold mb-3">Privacy Protection</h3>
              <p className="text-gray-600 text-sm leading-relaxed mb-4">
                Safeguard your personal information with advanced privacy features that keep your data secure and private.
              </p>
              <ul className="space-y-2">
                <li className="flex items-start gap-2 text-sm text-gray-700">
                  <i className="ri-check-line text-pink-500 mt-0.5"></i>
                  <span>IP address masking</span>
                </li>
                <li className="flex items-start gap-2 text-sm text-gray-700">
                  <i className="ri-check-line text-pink-500 mt-0.5"></i>
                  <span>DNS leak protection</span>
                </li>
              </ul>
            </div>
            <div className="bg-white rounded-2xl p-8 shadow-sm hover:shadow-xl transition-shadow">
              <div className="w-16 h-16 rounded-full bg-gradient-to-br from-teal-500 to-teal-600 flex items-center justify-center mb-6">
                <i className="ri-bar-chart-line text-white text-3xl"></i>
              </div>
              <h3 className="text-gray-900 text-xl font-bold mb-3">Real-Time Monitoring</h3>
              <p className="text-gray-600 text-sm leading-relaxed mb-4">
                Track your device performance and security status with comprehensive real-time analytics and insights.
              </p>
              <ul className="space-y-2">
                <li className="flex items-start gap-2 text-sm text-gray-700">
                  <i className="ri-check-line text-teal-500 mt-0.5"></i>
                  <span>Storage usage tracking</span>
                </li>
                <li className="flex items-start gap-2 text-sm text-gray-700">
                  <i className="ri-check-line text-teal-500 mt-0.5"></i>
                  <span>Connection statistics</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-32 px-8 bg-white">
        <div className="max-w-7xl mx-auto text-center">
          <h2 className="text-5xl font-extrabold text-black mb-5">
            Experience Next-Generation Mobile Security
          </h2>
          <p className="text-lg text-gray-600 mb-10 max-w-3xl mx-auto">
            Join thousands of users protecting their iOS devices with our advanced applications
          </p>
          <div className="inline-flex items-center gap-3 px-8 py-4 rounded-full bg-amber-900 text-white font-semibold">
            <span>Available on iOS</span>
            <i className="ri-arrow-right-line"></i>
          </div>
          <div className="mt-16 flex items-end justify-center gap-6">
            <div className="transform translate-y-4">
              <img src="https://readdy.ai/api/search-image?query=iPhone%20mockup%20displaying%20VPN%20application%20interface%20with%20connection%20button%20and%20server%20selection%20screen%2C%20modern%20iOS%20app%20design%20with%20dark%20theme%2C%20mobile%20security%20dashboard%2C%20clean%20UI%20with%20blue%20accent%20colors%2C%20professional%20product%20showcase&width=300&height=600&seq=dinagro-device-1&orientation=portrait" alt="VPN App Screen 1" className="w-48 rounded-3xl shadow-2xl" />
            </div>
            <div className="transform -translate-y-2">
              <img src="https://readdy.ai/api/search-image?query=iPhone%20mockup%20showing%20VPN%20app%20connected%20state%20with%20green%20checkmark%20and%20encrypted%20connection%20status%2C%20iOS%20security%20application%20interface%2C%20modern%20mobile%20UI%20design%20with%20gradient%20background%2C%20professional%20app%20screenshot&width=300&height=600&seq=dinagro-device-2&orientation=portrait" alt="VPN App Screen 2" className="w-48 rounded-3xl shadow-2xl" />
            </div>
            <div className="transform translate-y-0">
              <img src="https://readdy.ai/api/search-image?query=iPhone%20mockup%20displaying%20Cleaner%20application%20interface%20with%20storage%20analysis%20and%20optimization%20options%2C%20iOS%20cleaning%20app%20dashboard%20with%20colorful%20charts%2C%20modern%20mobile%20app%20design%20with%20orange%20accent%20colors%2C%20professional%20product%20showcase&width=300&height=600&seq=dinagro-device-3&orientation=portrait" alt="Cleaner App Screen 1" className="w-48 rounded-3xl shadow-2xl" />
            </div>
            <div className="transform translate-y-4">
              <img src="https://readdy.ai/api/search-image?query=iPhone%20mockup%20showing%20Cleaner%20app%20cleaning%20progress%20with%20animated%20interface%2C%20iOS%20optimization%20application%20with%20storage%20freed%20notification%2C%20modern%20mobile%20UI%20design%20with%20pink%20gradient%2C%20professional%20app%20screenshot&width=300&height=600&seq=dinagro-device-4&orientation=portrait" alt="Cleaner App Screen 2" className="w-48 rounded-3xl shadow-2xl" />
            </div>
            <div className="transform -translate-y-2">
              <img src="https://readdy.ai/api/search-image?query=iPhone%20mockup%20displaying%20app%20settings%20and%20privacy%20controls%20interface%2C%20iOS%20security%20and%20cleaning%20app%20configuration%20screen%2C%20modern%20mobile%20design%20with%20dark%20theme%2C%20professional%20product%20showcase%20with%20toggle%20switches&width=300&height=600&seq=dinagro-device-5&orientation=portrait" alt="App Settings Screen" className="w-48 rounded-3xl shadow-2xl" />
            </div>
          </div>
        </div>
      </section>

      {/* Contact Footer */}
      <footer id="contact" className="bg-black text-white py-20 px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-12 gap-16 mb-16">
            <div className="col-span-5">
              <h3 className="text-5xl font-extrabold mb-12">DINAGRO S.R.L.</h3>
              <p className="text-base leading-relaxed text-white/80 mb-10 max-w-md">
                Leading iOS application development company specializing in mobile security and optimization solutions for modern users
              </p>
              <div className="flex items-center gap-5">
                <div className="w-11 h-11 rounded-full border-2 border-white/30 flex items-center justify-center hover:bg-white/10 transition-colors cursor-pointer">
                  <i className="ri-linkedin-line text-xl"></i>
                </div>
                <div className="w-11 h-11 rounded-full border-2 border-white/30 flex items-center justify-center hover:bg-white/10 transition-colors cursor-pointer">
                  <i className="ri-twitter-x-line text-xl"></i>
                </div>
                <div className="w-11 h-11 rounded-full border-2 border-white/30 flex items-center justify-center hover:bg-white/10 transition-colors cursor-pointer">
                  <i className="ri-github-line text-xl"></i>
                </div>
                <div className="w-11 h-11 rounded-full border-2 border-white/30 flex items-center justify-center hover:bg-white/10 transition-colors cursor-pointer">
                  <i className="ri-medium-line text-xl"></i>
                </div>
              </div>
            </div>
            <div className="col-span-4">
              <div className="space-y-9">
                <div>
                  <p className="text-xs uppercase tracking-wider text-white/60 mb-3 flex items-center gap-2">
                    <i className="ri-map-pin-line"></i> ADDRESS
                  </p>
                  <p className="text-base">9 DE JULIO 733</p>
                  <p className="text-base">Crespo, E3116AFM</p>
                  <p className="text-base">Argentina</p>
                </div>
                <div>
                  <p className="text-xs uppercase tracking-wider text-white/60 mb-3 flex items-center gap-2">
                    <i className="ri-phone-line"></i> PHONE
                  </p>
                  <p className="text-base">+1 5189890739</p>
                </div>
                <div>
                  <p className="text-xs uppercase tracking-wider text-white/60 mb-3 flex items-center gap-2">
                    <i className="ri-mail-line"></i> EMAIL
                  </p>
                  <p className="text-base">information@dinagrosrl.com</p>
                </div>
              </div>
            </div>
            <div className="col-span-3">
              <img src="https://readdy.ai/api/search-image?query=Modern%20technology%20office%20interior%20with%20cool%20blue%20ambient%20lighting%2C%20contemporary%20workspace%20with%20sleek%20computers%20and%20mobile%20devices%2C%20professional%20tech%20company%20environment%20with%20glass%20walls%2C%20minimalist%20design%20aesthetic%2C%20futuristic%20office%20space&width=400&height=500&seq=dinagro-office-footer&orientation=portrait" alt="Office" className="w-full h-96 object-cover rounded-2xl" />
            </div>
          </div>
          <div className="border-t border-white/15 pt-8">
            <div className="flex items-center justify-between">
              <p className="text-sm text-white/80">© 2025 DINAGRO S.R.L. All rights reserved.</p>
              <div className="flex items-center gap-8">
                <span className="text-sm text-white/80 hover:text-white transition-colors cursor-pointer">Privacy Policy</span>
                <span className="text-sm text-white/80 hover:text-white transition-colors cursor-pointer">Terms of Service</span>
                <span className="text-sm text-white/80 hover:text-white transition-colors cursor-pointer">Cookies</span>
                <a href="https://readdy.ai/?ref=logo" target="_blank" rel="noopener noreferrer" className="text-sm text-white/80 hover:text-white transition-colors">Website Builder</a>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
